import React, { useState, useEffect } from 'react';
import './AssessmentGrading.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaClipboardList,
  FaUserGraduate,
  FaSearch,
  FaSave,
  FaCheck,
  FaTimes,
  FaChevronRight,
  FaFilter,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';

const DEFAULT_GRADE_SCALE = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

// Builds the option list for a grade <select>. If the value already saved on this
// item isn't part of the assessment's current scale (e.g. an admin changed the scale
// after this student was graded), it's kept as an extra "previous grade" option so it
// still displays correctly instead of silently going blank or getting overwritten.
const getGradeOptions = (assessment, currentGrade) => {
  const scaleGrades = assessment.gradeScale === 'custom' && assessment.customGrades?.length
    ? assessment.customGrades
    : DEFAULT_GRADE_SCALE;
  const isLegacy = currentGrade && !scaleGrades.includes(currentGrade);
  return { scaleGrades, legacyGrade: isLegacy ? currentGrade : null };
};

const AssessmentGrading = ({ user, assignedCourse, myStudentIds }) => {
  const [assessments, setAssessments] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [view, setView] = useState('selection');
  const [isSaving, setIsSaving] = useState(false);
  const [gradedStudentIds, setGradedStudentIds] = useState(new Set());

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (assignedCourse) {
      fetchAssessments();
      fetchStudents();
    }
  }, [assignedCourse]);

  useEffect(() => {
    if (selectedAssessment) {
      fetchGradedStudents();
    } else {
      setGradedStudentIds(new Set());
    }
  }, [selectedAssessment]);

  const fetchGradedStudents = async () => {
    if (!assignedCourse || !selectedAssessment) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/assessments/submissions/${assignedCourse._id}/${selectedAssessment.assessmentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // "Graded" means the teacher has submitted (not just saved a draft) —
        // matches the same status !== 'draft' convention used elsewhere in the app.
        const gradedIds = (data.submissions || [])
          .filter(sub => sub.status !== 'draft')
          .map(sub => sub.studentId);
        setGradedStudentIds(new Set(gradedIds));
      }
    } catch (error) {
      console.error('Error fetching graded students:', error);
    }
  };

  const fetchAssessments = async () => {
    if (!assignedCourse) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/assessments/course/${assignedCourse._id}/assessments`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
      } else {
        setError('Failed to fetch assessments');
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setError('Error loading assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    if (!assignedCourse) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/assessments/course/${assignedCourse._id}/students`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const loadSubmission = async () => {
    if (!selectedAssessment || !selectedStudent) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/assessments/submission/${assignedCourse._id}/${selectedAssessment.assessmentId}/${selectedStudent.studentId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCurrentSubmission(data.submission);
        setView('grading');
        
        // Scroll to top when grading form opens
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      } else {
        setError('Failed to load submission');
      }
    } catch (error) {
      console.error('Error loading submission:', error);
      setError('Error loading submission');
    } finally {
      setLoading(false);
    }
  };

  const saveSubmission = async (status = 'draft') => {
    if (!currentSubmission) return;
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/assessments/submission`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...currentSubmission,
            status
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCurrentSubmission(data.submission);
        setSuccess(data.message);
        
        if (status === 'submitted') {
          fetchGradedStudents();
          setTimeout(() => {
            setView('selection');
            setSelectedStudent(null);

            // Scroll to top when returning to selection
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 1500);
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save submission');
      }
    } catch (error) {
      console.error('Error saving submission:', error);
      setError('Error saving submission');
    } finally {
      setIsSaving(false);
    }
  };

  const updateGrade = (periodId, itemId, field, value) => {
    if (!currentSubmission) return;
    
    const updatedGrades = currentSubmission.grades.map(period => {
      if (period.periodId === periodId) {
        const updatedItems = period.items.map(item => {
          if (item.itemId === itemId) {
            if (field === 'score') {
              return { ...item, score: parseFloat(value) || null };
            } else if (field === 'grade') {
              return { ...item, grade: value };
            }
          }
          return item;
        });
        
        // Calculate total for yearly monthly assessments
        let total = null;
        if (currentSubmission.assessmentType === 'yearly' && currentSubmission.periodType === 'monthly') {
          total = updatedItems.reduce((sum, item) => sum + (item.score || 0), 0);
        }
        
        return { ...period, items: updatedItems, total };
      }
      return period;
    });
    
    setCurrentSubmission({
      ...currentSubmission,
      grades: updatedGrades
    });
  };

  const filteredStudents = students.filter(student => {
    if (!filterBatch || student.batch !== filterBatch) return false;

    const matchesSearch = searchTerm ?
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) : true;

    const matchesMyStudents = !myStudentIds || myStudentIds.length === 0 || myStudentIds.includes(student.studentId);

    return matchesSearch && matchesMyStudents;
  });

  const uniqueBatches = (() => {
    const minStartYear = new Date().getFullYear() - 4;
    return [...new Set(students.map(s => s.batch).filter(Boolean))]
      .filter(b => {
        const startYear = parseInt(b.split('-')[0]);
        return !isNaN(startYear) && startYear >= minStartYear;
      })
      .sort();
  })();

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Mobile view renderer
  const renderMobileGradingView = () => {
    if (!currentSubmission || !selectedAssessment) return null;

    const isMobile = window.innerWidth <= 480;
    if (!isMobile) return null;

    const isYearlyMonthly = currentSubmission.assessmentType === 'yearly' && currentSubmission.periodType === 'monthly';
    const isOverallGrade = currentSubmission.assessmentType === 'overall';

    return (
      <div className="mobile-grading-view55">
        {currentSubmission.grades.map((period, periodIndex) => (
          <div key={period.periodId} className="month-section55">
            <div className="month-header55">
              {period.periodLabel}
            </div>
            
            <div className="mobile-items-grid55">
              {selectedAssessment.assessmentItems.map((assessmentItem) => {
                const gradeItem = period.items.find(i => i.itemId === assessmentItem.id);
                return (
                  <div key={assessmentItem.id} className="mobile-item-row55">
                    <div className="mobile-item-name55">
                      {assessmentItem.name}
                    </div>
                    <div className="mobile-score-box55">
                      <span className="mobile-score-label55">
                        {isOverallGrade ? 'Grade' : 'Score'}
                      </span>
                      {isOverallGrade ? (() => {
                        const { scaleGrades, legacyGrade } = getGradeOptions(selectedAssessment, gradeItem?.grade);
                        return (
                          <select
                            value={gradeItem?.grade || ''}
                            onChange={(e) => updateGrade(period.periodId, assessmentItem.id, 'grade', e.target.value)}
                            className="grade-select55"
                          >
                            <option value="">-</option>
                            {legacyGrade && (
                              <option value={legacyGrade}>{legacyGrade} (previous grade)</option>
                            )}
                            {scaleGrades.map(grade => (
                              <option key={grade} value={grade}>{grade}</option>
                            ))}
                          </select>
                        );
                      })() : (
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.5"
                          value={gradeItem?.score !== null && gradeItem?.score !== undefined ? gradeItem.score : ''}
                          onChange={(e) => updateGrade(period.periodId, assessmentItem.id, 'score', e.target.value)}
                          className="score-input55"
                          placeholder="-"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Period Total for Yearly Monthly */}
            {isYearlyMonthly && (
              <div className="mobile-total-row55">
                <div className="mobile-total-label55">
                  Period Total
                </div>
                <div className="mobile-total-value55">
                  {(period.total || 0).toFixed(1)}
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Grand Total for Yearly Monthly */}
        {isYearlyMonthly && (
          <div className="mobile-grand-total55">
            <h4>Grand Total Score</h4>
            <p className="value">
              {currentSubmission.grades.reduce((sum, period) => sum + (period.total || 0), 0).toFixed(1)}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (!assignedCourse) {
    return (
      <div className="no-course-message55">
        <FaExclamationTriangle />
        <h3>No Course Assigned</h3>
        <p>Please contact your administrator to assign you to a course.</p>
      </div>
    );
  }

  const renderAssessmentPreview = (assessment) => (
    <div className="assessment-preview55">
      <div className="preview-header55">
        <h4>{assessment.name}</h4>
        <span className={`type-badge55 ${assessment.type}`}>
          {assessment.type === 'yearly' ? 'Yearly (Score)' : 'Grade-based'}
        </span>
      </div>
      <div className="preview-details55">
        {assessment.type === 'yearly' && (
          <>
            {assessment.yearNumber && !assessment.isOverallSheet && (
              <div className="detail-row55">
                <span className="detail-label55">Year:</span>
                <span className="detail-value55">Year {assessment.yearNumber}</span>
              </div>
            )}
            {assessment.isOverallSheet && (
              <div className="detail-row55">
                <span className="detail-label55">Sheet Type:</span>
                <span className="detail-value55 overall-badge55">Overall Sheet</span>
              </div>
            )}
            <div className="detail-row55">
              <span className="detail-label55">Periods:</span>
              <span className="detail-value55">
                {assessment.periodType === 'monthly' 
                  ? `${assessment.selectedMonths?.length || 0} months`
                  : `${assessment.selectedYears?.length || 0} years`}
              </span>
            </div>
          </>
        )}
        {assessment.type === 'overall' && (
          <div className="detail-row55">
            <span className="detail-label55">Grade Scale:</span>
            <span className="detail-value55">
              {assessment.gradeScale === 'custom' ? 
                assessment.customGrades.join(', ') : 
                'A - J'}
            </span>
          </div>
        )}
        <div className="detail-row55">
          <span className="detail-label55">Assessment Items:</span>
          <span className="detail-value55">{assessment.assessmentItems?.length || 0}</span>
        </div>
      </div>
    </div>
  );

  const handleBackToSelection = () => {
    setView('selection');
    
    // Scroll to top when returning to selection
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderGradingForm = () => {
    if (!currentSubmission || !selectedAssessment) return null;

    const isYearlyMonthly = currentSubmission.assessmentType === 'yearly' && currentSubmission.periodType === 'monthly';
    const isYearlyOverall = currentSubmission.assessmentType === 'yearly' && currentSubmission.periodType === 'yearly';
    const isOverallGrade = currentSubmission.assessmentType === 'overall';

    return (
      <div className="grading-form55">
        <div className="grading-header55">
          <button className="btn-back55" onClick={handleBackToSelection}>
            <FaTimes /> Back
          </button>
          <div className="grading-info55">
            <h3>{selectedAssessment.name}</h3>
            <p className="student-name55">{selectedStudent.name} ({selectedStudent.registrationNumber})</p>
            {selectedAssessment.yearNumber && (
              <span className="year-badge55">
                {selectedAssessment.isOverallSheet ? 'Overall Sheet' : `Year ${selectedAssessment.yearNumber}`}
              </span>
            )}
          </div>
        </div>

        {/* Academic Info Fields */}
        <div className="academic-info55">
          <div className="form-group55">
            <label>Unit</label>
            <input
              type="text"
              value={currentSubmission.unit || ''}
              onChange={(e) => setCurrentSubmission({
                ...currentSubmission,
                unit: e.target.value
              })}
              placeholder="Unit name"
            />
          </div>
          
          <div className="form-group55">
            <label>From Date</label>
            <input
              type="date"
              value={currentSubmission.fromDate ? 
                new Date(currentSubmission.fromDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setCurrentSubmission({
                ...currentSubmission,
                fromDate: e.target.value
              })}
            />
          </div>
          <div className="form-group55">
            <label>To Date</label>
            <input
              type="date"
              value={currentSubmission.toDate ? 
                new Date(currentSubmission.toDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setCurrentSubmission({
                ...currentSubmission,
                toDate: e.target.value
              })}
            />
          </div>
        </div>

        {/* Info Box */}
        <div className="info-box55">
          <FaInfoCircle />
          <div>
            {isYearlyMonthly && (
              <p>Enter scores (0-10) for each assessment item across the selected months.</p>
            )}
            {isYearlyOverall && (
              <p>Enter scores (0-10) for each assessment item across the years.</p>
            )}
            {isOverallGrade && (
              <p>
                Select a grade for each assessment item
                {selectedAssessment.gradeScale === 'custom' && selectedAssessment.customGrades?.length
                  ? ` (${selectedAssessment.customGrades.join(', ')})`
                  : ' (A-J)'}.
              </p>
            )}
          </div>
        </div>

        {/* Grading Table - Desktop View */}
        <div className="grading-table-wrapper55">
          <div className="grading-table-container55">
            <table className="grading-table55">
              <thead>
                <tr>
                  <th className="item-header55 sticky-col55">Assessment Items</th>
                  {currentSubmission.grades.map(period => (
                    <th key={period.periodId} className="period-header55">
                      {period.periodLabel}
                    </th>
                  ))}
                  {isYearlyMonthly && (
                    <th className="total-header55">Total</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {selectedAssessment.assessmentItems.map((assessmentItem) => (
                  <tr key={assessmentItem.id}>
                    <td className="item-name55 sticky-col55">{assessmentItem.name}</td>
                    {currentSubmission.grades.map(period => {
                      const gradeItem = period.items.find(i => i.itemId === assessmentItem.id);
                      return (
                        <td key={`${period.periodId}-${assessmentItem.id}`} className="grade-cell55">
                          {isOverallGrade ? (() => {
                            const { scaleGrades, legacyGrade } = getGradeOptions(selectedAssessment, gradeItem?.grade);
                            return (
                              <select
                                value={gradeItem?.grade || ''}
                                onChange={(e) => updateGrade(period.periodId, assessmentItem.id, 'grade', e.target.value)}
                                className="grade-select55"
                              >
                                <option value="">-</option>
                                {legacyGrade && (
                                  <option value={legacyGrade}>{legacyGrade} (previous grade)</option>
                                )}
                                {scaleGrades.map(grade => (
                                  <option key={grade} value={grade}>{grade}</option>
                                ))}
                              </select>
                            );
                          })() : (
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.5"
                              value={gradeItem?.score !== null && gradeItem?.score !== undefined ? gradeItem.score : ''}
                              onChange={(e) => updateGrade(period.periodId, assessmentItem.id, 'score', e.target.value)}
                              className="score-input55"
                              placeholder="-"
                            />
                          )}
                        </td>
                      );
                    })}
                    {isYearlyMonthly && (
                      <td className="total-cell55">
                        {currentSubmission.grades.reduce((sum, period) => {
                          const item = period.items.find(i => i.itemId === assessmentItem.id);
                          return sum + (item?.score || 0);
                        }, 0).toFixed(1)}
                      </td>
                    )}
                  </tr>
                ))}
                
                {/* Total Row for Yearly Monthly */}
                {isYearlyMonthly && (
                  <tr className="total-row55">
                    <td className="item-name55 sticky-col55"><strong>Period Total</strong></td>
                    {currentSubmission.grades.map(period => (
                      <td key={`total-${period.periodId}`} className="total-cell55">
                        <strong>{(period.total || 0).toFixed(1)}</strong>
                      </td>
                    ))}
                    <td className="grand-total-cell55">
                      <strong>
                        {currentSubmission.grades.reduce((sum, period) => sum + (period.total || 0), 0).toFixed(1)}
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile View */}
        {renderMobileGradingView()}

        {/* Comments Section */}
        <div className="comments-section55">
          <label>Comments (Optional)</label>
          <textarea
            value={currentSubmission.summary?.comments || ''}
            onChange={(e) => setCurrentSubmission({
              ...currentSubmission,
              summary: {
                ...currentSubmission.summary,
                comments: e.target.value
              }
            })}
            placeholder="Add any comments or feedback..."
            rows="4"
          />
        </div>

        {/* Action Buttons */}
        <div className="grading-actions55">
          <button 
            className="btn-save-draft55"
            onClick={() => saveSubmission('draft')}
            disabled={isSaving}
          >
            <FaSave /> Save Draft
          </button>
          <button 
            className="btn-submit55"
            onClick={() => saveSubmission('submitted')}
            disabled={isSaving}
          >
            <FaCheck /> Submit
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="assessment-grading-container55">
      {success && <div className="alert55 alert-success55">{success}</div>}
      {error && <div className="alert55 alert-error55">{error}</div>}

      {view === 'selection' && (
        <>
          <div className="page-header55">
            <h2>Assessment Grading</h2>
            <p>Select an assessment and student to begin grading</p>
          </div>

          <div className="selection-grid55">
            {/* Assessment Selection */}
            <div className="selection-panel55">
              <h3><FaClipboardList /> Select Assessment</h3>
              {loading ? (
                <div className="loading55">Loading assessments...</div>
              ) : assessments.length === 0 ? (
                <div className="empty-state55">
                  <p>No assessments available for this course</p>
                </div>
              ) : (
                <div className="assessments-list55">
                  {assessments.map(assessment => (
                    <div 
                      key={assessment.assessmentId}
                      className={`assessment-card55 ${selectedAssessment?.assessmentId === assessment.assessmentId ? 'selected55' : ''}`}
                      onClick={() => setSelectedAssessment(assessment)}
                    >
                      {renderAssessmentPreview(assessment)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Student Selection */}
            <div className="selection-panel55">
              <h3><FaUserGraduate /> Select Student</h3>
              
              <div className="student-filters55">
                <div className="search-box55">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search student..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="batch-filter-box55">
                  <FaFilter />
                  <select
                    value={filterBatch}
                    onChange={(e) => setFilterBatch(e.target.value)}
                  >
                    <option value="">Select Batch</option>
                    {uniqueBatches.map(batch => (
                      <option key={batch} value={batch}>{batch}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!filterBatch && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#888', background: '#f8f9fa', borderRadius: 8, margin: '12px 0' }}>
                  Please select a batch to view students.
                </div>
              )}

              <div className="students-list55">
                {filterBatch && filteredStudents.length === 0 ? (
                  <div className="empty-state55">
                    <p>No students found</p>
                  </div>
                ) : filterBatch ? (
                  filteredStudents.map(student => (
                    <div
                      key={student.studentId}
                      className={`student-card55 ${selectedStudent?.studentId === student.studentId ? 'selected55' : ''}`}
                      onClick={() => setSelectedStudent(student)}
                    >
                      <div className="student-info55">
                        <h4>{student.name}</h4>
                        <p>{student.registrationNumber}</p>
                        <span className="student-batch55">{student.batch}</span>
                      </div>
                      {selectedAssessment && gradedStudentIds.has(student.studentId) && (
                        <span className="graded-badge55">Graded</span>
                      )}
                      <FaChevronRight />
                    </div>
                  ))
                ) : null}
              </div>
            </div>
          </div>

          <div className="action-buttons55">
            <button 
              className="btn-proceed55"
              onClick={loadSubmission}
              disabled={!selectedAssessment || !selectedStudent || loading}
            >
              {loading ? 'Loading...' : 'Proceed to Grading'} <FaChevronRight />
            </button>
          </div>
        </>
      )}

      {view === 'grading' && renderGradingForm()}
    </div>
  );
};

export default AssessmentGrading;